import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import axios from "axios";

import { API } from "../utils/api";

const reveal = {
  hidden: { opacity: 0, y: 40 },
  show: (i) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.9, delay: i * 0.1, ease: [0.16, 1, 0.3, 1] },
  }),
};

const pluralGens = (n) =>
  n % 10 === 1 && n % 100 !== 11
    ? "примерка"
    : n % 10 >= 2 && n % 10 <= 4 && (n % 100 < 12 || n % 100 > 14)
      ? "примерки"
      : "примерок";

export const Pricing = () => {
  const [plans, setPlans] = useState([]);
  useEffect(() => {
    axios.get(`${API}/plans`).then(({ data }) => setPlans(data)).catch(() => {});
  }, []);
  if (!plans.length) return null;

  // выгода длинного тарифа против его помесячного аналога
  const benefitOf = (p) => {
    const base = plans.find((q) => q.title === p.title && q.months === 1);
    if (!base || !base.price_rub) return null;
    const perMonth = Math.round(p.price_rub / p.months);
    const win = Math.round((1 - perMonth / base.price_rub) * 100);
    return { perMonth, win };
  };

  return (
    <section
      id="pricing"
      className="relative overflow-hidden px-6 pb-16 pt-28 md:px-12 md:py-40 lg:px-24"
      data-testid="pricing-section"
    >
      <div
        aria-hidden="true"
        className="pointer-events-none absolute right-0 top-1/3 h-[500px] w-[500px] rounded-full bg-[#C2A878]/[0.04] blur-[120px]"
      />
      <div className="relative grid gap-16 lg:grid-cols-12 lg:gap-12">
        <div className="lg:col-span-4">
          <motion.p
            variants={reveal}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, margin: "-80px" }}
            custom={0}
            className="mb-8 font-jetbrains text-xs uppercase tracking-[0.35em] text-[#C2A878]"
          >
            ( Тарифы )
          </motion.p>
          <motion.h2
            variants={reveal}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, margin: "-80px" }}
            custom={1}
            className="font-cormorant text-4xl font-light leading-[1.05] tracking-tight text-[#F4F4F0] md:text-6xl"
          >
            Выберите свой <span className="italic text-[#C2A878]">формат доступа</span>
          </motion.h2>
          <motion.p
            variants={reveal}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, margin: "-80px" }}
            custom={2}
            className="mt-8 max-w-sm text-base leading-relaxed text-[#A3A3A3]"
          >
            Маски и линии бесплатны всегда, а первая примерка — в подарок
            за знакомство. Тариф открывает ИИ-примерки новой формы
            на вашем фото и сохранение результатов, а «Про» — ещё и технологические
            карты стрижек для мастера.
          </motion.p>
          <motion.p
            variants={reveal}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, margin: "-80px" }}
            custom={3}
            className="mt-8 font-jetbrains text-[10px] uppercase tracking-[0.25em] text-[#525252]"
          >
            Карта · СБП · доступ сразу после оплаты
          </motion.p>
        </div>

        <div className="lg:col-span-8">
          {plans.map((p, i) => (
            <motion.div
              key={p.id}
              variants={reveal}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true, margin: "-60px" }}
              custom={i}
              className="group border-t border-white/10 py-10 transition-colors duration-500 last:border-b hover:border-[#C2A878]/30"
              data-testid={`pricing-plan-${p.id}`}
            >
              <div className="flex flex-col gap-6 md:flex-row md:items-start md:justify-between">
                <div className="flex items-start gap-6">
                  <span className="pt-2 font-jetbrains text-xs tracking-[0.2em] text-[#525252] transition-colors duration-500 group-hover:text-[#C2A878]">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <div>
                    <div className="flex flex-wrap items-baseline gap-x-4 gap-y-2">
                      <h3 className={`font-cormorant text-3xl font-light md:text-4xl ${p.is_pro ? "italic text-[#C2A878]" : "text-[#F4F4F0]"}`}>
                        {p.title}
                      </h3>
                      <span className="font-jetbrains text-[10px] uppercase tracking-[0.25em] text-[#525252]">
                        {p.period}
                      </span>
                      {p.is_pro && (
                        <span className="border border-[#C2A878]/50 px-2 py-0.5 font-jetbrains text-[8px] uppercase tracking-[0.25em] text-[#C2A878]">
                          {p.months >= 12 ? "2 месяца в подарок" : "Флагман"}
                        </span>
                      )}
                    </div>
                    <p className="mt-3 max-w-md font-jetbrains text-[11px] uppercase leading-relaxed tracking-[0.15em] text-[#A3A3A3]">
                      {p.generations} {pluralGens(p.generations)} в месяц · разбор по 68 точкам · все маски · сохранение в HD{p.is_pro ? " · техкарты" : ""}
                    </p>
                    {p.months > 1 && benefitOf(p) && (
                      <p
                        className="mt-2 font-jetbrains text-[10px] uppercase tracking-[0.2em] text-[#C2A878]"
                        data-testid={`pricing-benefit-${p.id}`}
                      >
                        ≈ {benefitOf(p).perMonth} ₽/мес{benefitOf(p).win > 0 ? ` · выгода ${benefitOf(p).win}%` : ""}
                      </p>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-6 pl-10 md:shrink-0 md:flex-col md:items-end md:gap-4 md:pl-0">
                  <p className="font-cormorant text-4xl font-light leading-none text-[#F4F4F0]">
                    {p.price_rub}
                    <span className="ml-1 text-xl italic text-[#A3A3A3]">₽</span>
                  </p>
                  <Link
                    to="/account"
                    className="border border-[#C2A878] px-6 py-2.5 font-jetbrains text-[10px] uppercase tracking-[0.25em] text-[#C2A878] transition-colors duration-500 hover:bg-[#C2A878] hover:text-[#0A0A0A]"
                    data-testid={`pricing-buy-${p.id}`}
                  >
                    Оформить
                  </Link>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        <p className="mt-4 text-center font-jetbrains text-[11px] tracking-[0.08em] text-[#525252] lg:col-span-12">
          Оплата картой или СБП через CloudPayments · доступ сразу после оплаты · без автопродлений
        </p>
      </div>
    </section>
  );
};
